import type { CacheHeaders } from '#types/plugin/cache'
import type { RouteLocationNormalizedLoaded } from 'vue-router'
import apiClientFactory from '#root/api'

/**
 * Retrieves headers for API requests.
 * @returns The headers object for API requests.
 * @category Utils
 */
export const useApiHeaders = () => {
  const { brandPrefix, clientApp } = useAppConfig().api
  return getApiHeaders(brandPrefix, clientApp, useCountryCode())
}

const getContentQuery = (env: string, target: string) => {
  const contentTarget = (env !== 'PROD' || target === 'production_test') && target
  return contentTarget ? { cms: contentTarget } : {} as any
}

const setupAPIHeaders = () => {
  const locale = useLocale()
  const settings = useApiHeaders() || {}
  const correlationId = uuid()
  const { enableFApiProxy } = useFeatureFlags()
  const reqHeaders = useRequestHeaders()
  const { authAccessKey, ssrTrustId } = useRuntimeConfig().api || {}
  const { isDev } = useRuntimeConfig().public

  return {
    ...settings,
    'Accept': 'application/json',
    'x-transaction-id': correlationId,
    'x-correlation-id': correlationId,
    'x-auth-access': authAccessKey || reqHeaders?.['x-auth-access'] || '',
    'vf-server-trust-id': ssrTrustId,
    'locale': (isDev || enableFApiProxy) ? locale : getApiLocale(locale),
    ...(reqHeaders?.['bg-testing'] && { 'bg-testing': reqHeaders['bg-testing'] }),
    ...(reqHeaders?.['x-bypass-maintenance'] && { 'x-bypass-maintenance': reqHeaders['x-bypass-maintenance'] }),
    ...(isDev && reqHeaders?.['x-disable-human'] ? { 'x-disable-human': reqHeaders['x-disable-human'] } : {}),
  } as Record<string, string>
}

const setupCMSHeaders = (_route?: RouteLocationNormalizedLoaded) => {
  const route = _route || useRoute()
  const reqHeaders = useRequestHeaders()
  const { authAccessKey } = useRuntimeConfig().api || {}
  const contentPreviewDate = route.query.previewDateRFC1123 as string

  return {
    ...(contentPreviewDate ? { 'X-Preview-Date': contentPreviewDate } : {}),
    'CoreMedia-PreviewAccessToken': 'FirstCoreMediaSandBox',
    'x-auth-access': authAccessKey || reqHeaders?.['x-auth-access'] || '',
  } as Record<string, string>
}

export const useApi = (_route?: RouteLocationNormalizedLoaded) => {
  const locale = useLocale()
  const route = _route || useRoute()
  const host = useHost()
  const usidCookie = useCookie('x-usid')
  const previewDateTimeCookie = useCookie('previewDateTime')
  const {
    enableAPIPreview,
    enableFApiProxy,
    enableFContentProxy,
    configApiDomains = {
      apiCutoverUrlSSR: undefined,
      cmsCutoverUrlSSR: undefined,
      apiBaseUrl: '',
      cmsBaseUrl: ''
    },
    configCacheTagControlHeaders
  } = useFeatureFlags()

  const apiBaseUrl = (isServer() && configApiDomains.apiCutoverUrlSSR) || configApiDomains.apiBaseUrl
  const cmsBaseUrl = (isServer() && configApiDomains.cmsCutoverUrlSSR) || configApiDomains.cmsBaseUrl

  let apiUrlSuffix = ''
  if (enableAPIPreview && (route.query.preview === 'true' || previewDateTimeCookie.value))
    apiUrlSuffix = '/pre'

  const { apiRoutePrefix, cmsApiRoutePrefix, isDev, targetEnv } = useRuntimeConfig().public

  const monetateBaseUrl = host.includes('localhost') ? 'https://engine.monetate.net' : '/decision'

  const apiHeaders = setupAPIHeaders()
  const cmsHeaders = setupCMSHeaders(route)

  const usidHeader = {
    // the x-usid cookie value is set on the Auth middleware
    // and cleared on sign-out on the Auth Store
    'x-usid': usidCookie.value || uuid()
  }

  // This is an object where every key from the API settings has an empty string value
  // and can be used to override the base headers where needed
  // (e.g., Monetate breaks if a locale header is sent)
  const clearedApiHeaders = Object.entries(apiHeaders).reduce((acc, [key]) => ({ ...acc, [key]: '' }), {})

  const cacheHeaders = useState<CacheHeaders>('cacheHeaders')

  return apiClientFactory({
    base: {
      baseURL: `${(isDev || enableFApiProxy) ? apiRoutePrefix : apiBaseUrl}${apiUrlSuffix}`,
      headers: {
        ...apiHeaders,
        cookie: useRequestHeaders()?.cookie || ''
      },
      credentials: 'same-origin',
      query: {
        locale,
        ...(route.query.bypassAllCache === 'true' && { bypassAllCache: true })
      },
      onRequest({ options }) {
        if (!usidCookie.value)
          options.headers.delete('x-usid')

        if (options.headers.get('x-transaction-id')) {
          const id = uuid()
          options.headers.set('x-transaction-id', id)
          options.headers.set('x-correlation-id', id)
        }
      }
    },
    authentication: {
      headers: usidHeader
    },
    content: {
      baseURL: (isDev || enableFContentProxy) ? cmsApiRoutePrefix : cmsBaseUrl,
      query: {
        ...getContentQuery(targetEnv, route.query.cms as string),
        removeNulls: true
      },
      headers: {
        ...cmsHeaders
      },
      credentials: 'same-origin',
      onResponse(responseData) {
        // This callback is attached to CMS endpoints only
        // Save cache headers
        if (isServer()) {
          const cacheTags = ['Netlify-Cache-Tag', 'Edge-Cache-Tag', 'Netlify-CDN-Cache-Control', 'Akamai-Cache-Control']

          cacheTags.forEach((tag) => {
            const cacheHeader = responseData.response.headers.get(tag)

            if (cacheHeader && configCacheTagControlHeaders[tag].enabled)
              cacheHeaders.value[tag].push(cacheHeader)
          })
        }
      }
    },
    consumer: {
      headers: usidHeader
    },
    internal: {
      baseURL: apiRoutePrefix
    },
    wishlists: {
      headers: {
        ...usidHeader,
        // This is required for removing an item from the wishlist,
        // the API requires a content type, but the DELETE request has no body
        // so the client can't know to set it automatically
        'content-type': 'application/json'
      }
    },
    decide: {
      baseURL: monetateBaseUrl,
      headers: {
        ...clearedApiHeaders,
        Accept: 'application/json',
      }
    },
    socialNative: {
      baseURL: 'https://photorankapi-a.akamaihd.net',
      headers: {
        ...clearedApiHeaders,
        Accept: 'application/json',
      }
    }
  })
}
